import type {
  IFilterOptions,
  IOptions,
  IOptionsProps,
  IUseFilterProps,
} from "@fluidattacks/design";
import { useFilters } from "@fluidattacks/design";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

import type { ToePortsFiltersInput } from "gql/graphql";
import type { IToePortData } from "pages/group/surface/ports/types";
import { useFiltersStorage } from "storage/filters";
import {
  getCheckedFilter,
  getDateRangeFilter,
  getNumberRangeFilter,
} from "utils/filters";
import { formatBoolean, formatStatus } from "utils/format-helpers";

const useToEPortsFilters = ({
  dataset,
  groupName,
  setFilteredDataset,
  setBackFilters,
}: Readonly<{
  dataset: IToePortData[];
  groupName: string;
  setFilteredDataset: React.Dispatch<React.SetStateAction<IToePortData[]>>;
  setBackFilters: React.Dispatch<React.SetStateAction<ToePortsFiltersInput>>;
}>): IUseFilterProps<IToePortData> => {
  const { t } = useTranslation();
  const { setValues, getValues } = useFiltersStorage();

  const options: IOptionsProps<IToePortData>[] = useMemo(
    (): IOptionsProps<IToePortData>[] => [
      {
        filterOptions: [
          {
            key: "rootNickname",
            label: t("group.toe.ports.root"),
            options: Array.from(
              new Set(dataset.map((toePort): string => toePort.rootNickname)),
            ).map(
              (nickname): IOptions => ({ label: nickname, value: nickname }),
            ),
            type: "select",
          },
        ],
        label: t("group.toe.ports.root"),
      },
      {
        filterOptions: [
          {
            key: "address",
            label: t("group.toe.ports.address"),
            options: Array.from(
              new Set(dataset.map((toePort): string => toePort.address)),
            ).map((address): IOptions => ({ label: address, value: address })),
            type: "select",
          },
        ],
        label: t("group.toe.ports.address"),
      },
      {
        filterOptions: [
          {
            key: "port",
            label: t("group.toe.ports.port"),
            type: "numberRange",
          },
        ],
        label: t("group.toe.ports.port"),
      },
      {
        filterOptions: [
          {
            key: "hasVulnerabilities",
            label: t("group.toe.ports.status"),
            options: [
              { label: formatStatus(true), value: "true" },
              { label: formatStatus(false), value: "false" },
            ],
            type: "radioButton",
          },
        ],
        label: t("group.toe.ports.status"),
      },
      {
        filterOptions: [
          {
            key: "seenAt",
            label: t("group.toe.ports.seenAt"),
            type: "dateRange",
          },
        ],
        label: t("group.toe.ports.seenAt"),
      },
      {
        filterOptions: [
          {
            key: "bePresent",
            label: t("group.toe.ports.bePresent"),
            options: [
              { label: formatBoolean(true), value: "true" },
              { label: formatBoolean(false), value: "false" },
            ],
            type: "radioButton",
          },
        ],
        label: t("group.toe.ports.bePresent"),
      },
    ],
    [dataset, t],
  );

  const handleGetValues = useCallback((): string | null => {
    return getValues(`toe_ports-${groupName}`) as string | null;
  }, [getValues, groupName]);
  const handleSetValues = useCallback(
    (value: string): void => {
      void setValues(`toe_ports-${groupName}`, value);
    },
    [setValues, groupName],
  );

  const handleFilterChange = useCallback(
    (newFilters: readonly IFilterOptions<IToePortData>[]): void => {
      const newFiltersArray = newFilters as IFilterOptions<IToePortData>[];
      const address = getCheckedFilter(newFiltersArray, "address")?.value;
      const root = getCheckedFilter(newFiltersArray, "rootNickname")?.value;
      const bePresent = getCheckedFilter(newFiltersArray, "bePresent")?.value;
      const hasVulnerabilities = getCheckedFilter(
        newFiltersArray,
        "hasVulnerabilities",
      )?.value;
      const { min: minPortValue, max: maxPortValue } = getNumberRangeFilter(
        newFiltersArray,
        "port",
      );
      const { from: fromSeenAtValue, to: toSeenAtValue } = getDateRangeFilter(
        newFiltersArray,
        "seenAt",
      );

      setBackFilters({
        address,
        bePresent: bePresent === undefined ? undefined : bePresent === "true",
        fromSeenAt: fromSeenAtValue ?? undefined,
        hasVulnerabilities:
          hasVulnerabilities === undefined
            ? undefined
            : hasVulnerabilities === "true",
        maxPort: maxPortValue === undefined ? undefined : String(maxPortValue),
        minPort: minPortValue === undefined ? undefined : String(minPortValue),
        rootNickname: root === "" ? undefined : root,
        toSeenAt: toSeenAtValue ?? undefined,
      });
    },
    [setBackFilters],
  );

  return useFilters({
    dataset,
    getValues: handleGetValues,
    onFiltersChange: handleFilterChange,
    options,
    setFilteredDataset,
    setValues: handleSetValues,
  });
};

export { useToEPortsFilters };
